/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation } from '@apollo/client'

import {
  PARSE_PSA_FILE,
  ParsePsaFileMutation,
  ParsePsaFileMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

import { ImportedPlanogram } from '../helpers/import-planogram.helpers'

interface UsePsaFileUploadOptions {
  onSuccess: (planograms: ImportedPlanogram[]) => void
  onError: (message: string) => void
  onLoadingChange: (loading: boolean) => void
}

export const usePsaFileUpload = ({
  onSuccess,
  onError,
  onLoadingChange,
}: UsePsaFileUploadOptions) => {
  const { t } = useT()

  const [parsePsaFileMutation] = useMutation<
    ParsePsaFileMutation,
    ParsePsaFileMutationVariables
  >(PARSE_PSA_FILE)

  const handlePsaFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    onLoadingChange(true)

    try {
      const result = await parsePsaFileMutation({ variables: { file } })
      const parsed = result.data?.parsePsaFile

      if (!parsed) throw new Error(t('importPlanogram.failedToParsePSA'))

      const imported: ImportedPlanogram[] = parsed.segments.map(seg => ({
        door: seg.segmentIndex + 1,
        shelves: seg.shelves.map(shelf => ({
          shelf: shelf.segmentShelfIndex + 1,
          products: shelf.facings.map(f => ({
            upc: f.upc,
            name: f.upc,
            facingQuantity: f.quantity,
          })),
        })),
      }))

      onSuccess(imported)
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : t('importPlanogram.failedToParsePSA'),
      )
    } finally {
      onLoadingChange(false)
    }
  }

  return { handlePsaFileUpload }
}
